import { useEffect, useRef, useState } from "react";
import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { OrderDoneCelebration } from "./subcomponents/OrderDoneCelebration";
import { BoxHeaderTypography } from "../../components/Typographies/BoxHeaderTypography";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import {
  AwaitingPaymentNotice,
  orderAwaitsStripePayment,
} from "../../components/Order/PayNow";

export function OrderPlaced() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get("orderNumber");

  // Placement runs on a queue: a vendor org with credit limit 0 gets its
  // order diverted to Stripe prepayment seconds after this screen renders.
  // Poll for the placed order so the "Pay now" state shows up immediately,
  // without the user having to find the order list and refresh.
  const { orderRepository } = useRepositoriesContext();
  const { data: placedOrder } = orderRepository.useOrderByNumber(orderNumber);
  const awaitsPayment = placedOrder && orderAwaitsStripePayment(placedOrder);

  // Nichlas 11/7: a zero-credit vendor must not have to find and click
  // "Pay now" — the moment the queued placement parks the order in
  // Awaiting payment, take the user straight to the Stripe Checkout.
  // Same-tab so the return URL (?stripe=success) lands back in the app.
  // Runs at most once; if the user comes back without paying (browser
  // back / cancelled checkout), pageshow resets the state and the
  // banner with the Pay now button below is the fallback.
  const [redirecting, setRedirecting] = useState(false);
  const redirectedRef = useRef(false);
  useEffect(() => {
    const url = placedOrder?.stripePaymentUrl;
    if (!awaitsPayment || !url || redirectedRef.current) return;
    redirectedRef.current = true;
    setRedirecting(true);
    window.location.assign(url);
  }, [awaitsPayment, placedOrder]);
  useEffect(() => {
    function handlePageShow(e: PageTransitionEvent) {
      if (e.persisted) setRedirecting(false);
    }
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const navigate = useNavigate();
  function handleNavigateToOrders() {
    navigate("/order");
  }

  return (
    <JetSportBox>
      <BoxHeader2>
        <BoxHeaderTypography className={"uppercase"}>
          {t("orderListDetails.orderConfirmed")}
        </BoxHeaderTypography>
      </BoxHeader2>
      <BoxContent className={"pt-12 pb-32 items-center"}>
        {awaitsPayment && (
          <div className={"w-[515px] mb-4"}>
            {redirecting ? (
              <p className={"text-center font-semibold"}>
                {t("orderPayment.redirectingToPayment")}
              </p>
            ) : (
              <AwaitingPaymentNotice order={placedOrder!} showButton />
            )}
          </div>
        )}
        <OrderDoneCelebration
          orderId={orderNumber ?? ""}
          handleNavigateToOrders={handleNavigateToOrders}
          showCustomerFillMessage={false}
        />
      </BoxContent>
    </JetSportBox>
  );
}
